import { NgModule } from "@angular/core";
import { ExtraOptions, RouterModule, Routes } from "@angular/router";
import { AppComponent } from "./app.component";

import * as fromGuards from "./guards";

export const routingConfiguration: ExtraOptions = {
  paramsInheritanceStrategy: "always",
};

const routes: Routes = [
  // { path: '', component: AppComponent },
  {
    path: "auth",
    loadChildren: () =>
      import("./views/auth/auth.module").then((module) => module.AuthModule),
  },
  // QSL-9973 (Admin Nav Phase 5): the /crm, /engineer and /reviewer shells
  // have been sunset. Their canonical replacement is /super-admin/* served
  // by SuperAdminModule. Any leftover deep links are redirected by the
  // catch-all blocks below so that bookmarks and old emails still work.
  {
    path: "crm",
    redirectTo: "super-admin",
    pathMatch: "full",
  },
  {
    path: "crm/:rest",
    redirectTo: "super-admin/:rest",
  },
  {
    path: "reviewer",
    redirectTo: "super-admin",
    pathMatch: "full",
  },
  {
    path: "reviewer/:rest",
    redirectTo: "super-admin/:rest",
  },
  {
    path: "engineer",
    redirectTo: "super-admin",
    pathMatch: "full",
  },
  {
    path: "engineer/:rest",
    redirectTo: "super-admin/:rest",
  },
  {
    path: "super-admin",
    loadChildren: () =>
      import("./views/super-admin/super-admin.module").then(
        (module) => module.SuperAdminModule
      ),
    canActivate: [fromGuards.AuthGuardService, fromGuards.SuperAdminGuard],
  },
  {
    path: "teacher",
    loadChildren: () =>
      import("./views/teacher/teacher.module").then(
        (module) => module.TeacherModule
      ),
    canActivate: [fromGuards.AuthGuardService, fromGuards.TeacherGuard],
  },
  {
    path: "member",
    loadChildren: () =>
      import("./views/member/member.module").then(
        (module) => module.MemberModule
      ),
    canActivate: [fromGuards.AuthGuardService, fromGuards.MemberGuard],
  },
  {
    path: "my-stats",
    loadChildren: () =>
      import("./views/member-stats/member-stats.module").then(
        (module) => module.MemberStatsModule
      ),
    canActivate: [fromGuards.AuthGuardService],
  },
  {
    path: "siraj",
    loadChildren: () =>
      import("./views/super-admin/views/siraj/siraj.module").then(
        (module) => module.SirajModule
      ),
    canActivate: [fromGuards.AuthGuardService], // Only requires valid token, no role check
  },
  {
    path: "guest",
    loadChildren: () =>
      import("./views/super-admin/views/siraj/siraj.module").then(
        (module) => module.SirajModule
      ),
    // No guards - public access for demos and sharing
  },
  {
    path: "siraj/guest",
    loadChildren: () =>
      import("./views/super-admin/views/siraj/siraj.module").then(
        (module) => module.SirajModule
      ),
    // No guards - public access for mobile WebView guest mode
  },
  {
    path: "trial",
    loadChildren: () =>
      import("./views/super-admin/views/siraj/siraj.module").then(
        (module) => module.SirajModule
      ),
    data: { isTrial: true },
    // No guards - public access for QT trial participants
  },
  {
    path: "privacy-policy",
    loadChildren: () =>
      import("./views/privacy-policy/privacy-policy.module").then(
        (module) => module.PrivacyPolicyModule
      ),
  },
  {
    path: "email-preferences",
    loadChildren: () =>
      import(
        "./shared-modules/email-preferences/email-preferences.module"
      ).then((module) => module.EmailPreferencesModule),
    canActivate: [fromGuards.AuthGuardService],
  },
  {
    path: "unsubscribe",
    loadChildren: () =>
      import(
        "./shared-modules/unsubscribe/unsubscribe.module"
      ).then((module) => module.UnsubscribeModule),
  },
  {
    path: "resubscribe",
    loadChildren: () =>
      import(
        "./shared-modules/resubscribe/resubscribe.module"
      ).then((module) => module.ResubscribeModule),
  },
  // Gifting WebView - loaded inside React Native WebView
  {
    path: "gifting",
    loadChildren: () =>
      import("./views/gifting/gifting.module").then(
        (module) => module.GiftingModule
      ),
    // No guards - public access for mobile WebView (auth via query params)
  },
  // gifting/guest is handled as a child route inside GiftingRoutingModule
  // Sponsorship WebView - loaded inside React Native WebView (QSL-10194)
  {
    path: "sponsorship",
    loadChildren: () =>
      import("./views/sponsorship/sponsorship.module").then(
        (module) => module.SponsorshipModule
      ),
    // No guards - public access for mobile WebView (auth via query params)
  },
  // sponsorship/guest is handled as a child route inside SponsorshipRoutingModule
  // Gift Redemption Landing Page - public access via gift code link
  {
    path: "gift/:code",
    loadChildren: () =>
      import("./views/gift-redeem/gift-redeem.module").then(
        (module) => module.GiftRedeemModule
      ),
    // No guards - public access for gift redemption links
  },
  // QSL-9969 (Admin Nav Phase 1): standalone visual preview for the new
  // UnifiedSideNavComponent. Not linked from anywhere; reachable only via
  // the explicit /admin/_navtest URL. Removed in Phase 5 sunset.
  {
    path: "admin/_navtest",
    loadChildren: () =>
      import(
        "./shared-modules/unified-side-nav/nav-test/nav-test.module"
      ).then((module) => module.NavTestModule),
  },
  { path: "", redirectTo: "super-admin", pathMatch: "full" },
  {
    path: "**",
    loadChildren: () =>
      import("./views/not-found/not-found.module").then(
        (module) => module.NotFoundModule
      ),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, routingConfiguration)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
